import { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { SIDEBAR_ACTIONS } from "../../../../constants/actionsConstants";
import showtoast from "../../../../util/toast";
import WhatsappFlowModelMC from "../../../models/WhatsappFlowModelMC";
import CustomerModel from "../../../../components/Customers/CustomerModel";

export const useWhatsappFlowResponses = (flow, initialTab, onSaved) => {
  const { t } = useTranslation('WhatsappFlowsScreenMC');
  const dispatch = useDispatch();

  const methods = useForm({
    defaultValues: {
      defaultHeaderText: flow?.default_header_text || "",
      defaultFlowCta: flow?.default_flow_cta || "",
      defaultBodyText: flow?.default_body_text || "",
      defaultFooterText: flow?.default_footer_text || "",
    }
  });

  useEffect(() => {
    setDefaultHeaderText(flow?.default_header_text || "");
    methods.reset({
      defaultHeaderText: flow?.default_header_text || "",
      defaultFlowCta: flow?.default_flow_cta || "",
      defaultBodyText: flow?.default_body_text || "",
      defaultFooterText: flow?.default_footer_text || "",
    });
  }, [flow, methods.reset]);

  const [activeTab, setActiveTab] = useState(initialTab || "responses");
  const [responses, setResponses] = useState([]);
  const [customerFieldsOptions, setCustomerFieldsOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Date filter state — default last 30 days
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(formatDate(thirtyDaysAgo));
  const [endDate, setEndDate] = useState(formatDate(today));

  const dateRangeValue = useMemo(() => {
    const parseDateStr = (str, fallback) => {
      if (!str) return fallback;
      const parts = str.split('-');
      return new Date(parts[0], parts[1] - 1, parts[2]);
    };
    return {
      startDate: parseDateStr(startDate, thirtyDaysAgo),
      endDate: parseDateStr(endDate, today)
    };
  }, [startDate, endDate]);

  const handleDateRangeChange = ({ value }) => {
    if (value && value.startDate && value.endDate) {
      setStartDate(formatDate(value.startDate));
      setEndDate(formatDate(value.endDate));
    } else {
      setStartDate(formatDate(thirtyDaysAgo));
      setEndDate(formatDate(today));
    }
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Mapping state
  const [saveToCustomerState, setSaveToCustomerState] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [parsedFlowKeys, setParsedFlowKeys] = useState(flow?.raw_data?.detected_fields || []);

  const [newFlowKey, setNewFlowKey] = useState("");
  const [newCustomerField, setNewCustomerField] = useState("");

  const [defaultHeaderText, setDefaultHeaderText] = useState(flow?.default_header_text || "");
  const [defaultFlowCta, setDefaultFlowCta] = useState(flow?.default_flow_cta || "");
  const [defaultBodyText, setDefaultBodyText] = useState(flow?.default_body_text || "");
  const [defaultFooterText, setDefaultFooterText] = useState(flow?.default_footer_text || "");

  const componentKey = initialTab === "mapping" ? "WhatsappFlowMappingModal" : "WhatsappFlowResponsesModal";

  const closeModal = () => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: { componentKey }
    });
  };

  // Reset page when active flow or dates change
  useEffect(() => {
    setCurrentPage(1);
  }, [flow.meta_flow_id, startDate, endDate]);

  // Fetch customer columns once when active flow changes
  useEffect(() => {
    const fetchColumns = async () => {
      try {
        const colsData = await CustomerModel.getCustomerColumns();
        const baseFields = colsData?.fields || [];
        const hasNotes = baseFields.some(([_, id]) => id === 'notes');
        const hasCountry = baseFields.some(([_, id]) => id === 'country_id');

        const extraFields = [];
        if (!hasNotes) extraFields.push([t('fields.notes'), 'notes']);
        if (!hasCountry) extraFields.push([t('fields.country_id'), 'country_id']);
        extraFields.push([t('fields.customer_documents'), 'customer_documents']);

        const allFields = [...baseFields, ...extraFields];

        const fields = allFields
          .filter(([_, id]) => id !== 'phone')
          .map(([label, id]) => ({
            id,
            label
          }));
        setCustomerFieldsOptions(fields);
      } catch (e) {
        console.error("Error fetching customer columns", e);
      }
    };
    fetchColumns();
  }, [flow.meta_flow_id]);

  // Fetch paginated responses from the backend when page or active flow changes
  useEffect(() => {
    if (activeTab !== "responses") {
      setLoading(false);
      return;
    }

    const fetchPageResponses = async () => {
      setLoading(true);
      try {
        const params = { page: currentPage };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const respData = await WhatsappFlowModelMC.getResponses(flow.meta_flow_id, params);
        setResponses(respData?.responses || []);
        setTotalPages(respData?.total_pages || 1);
      } catch (e) {
        console.error("Error fetching responses page", e);
      } finally {
        setLoading(false);
      }
    };
    fetchPageResponses();
  }, [flow.meta_flow_id, currentPage, activeTab, startDate, endDate]);

  useEffect(() => {
    if (flow?.fields_mapping && Object.keys(flow.fields_mapping).length > 0) {
      const initialMappings = Object.entries(flow.fields_mapping).map(([flowKey, customerField]) => ({
        flowKey,
        customerField
      }));
      setMappings(initialMappings);
      setSaveToCustomerState(true);
    } else {
      setMappings([]);
      setSaveToCustomerState(false);
    }
    setParsedFlowKeys(flow?.raw_data?.detected_fields || []);
    setDefaultFlowCta(flow?.default_flow_cta || "");
    setDefaultBodyText(flow?.default_body_text || "");
    setDefaultFooterText(flow?.default_footer_text || "");
  }, [flow]);

  // Subscribe to websocket event for flow export completion
  useEffect(() => {
    const handleExportCompleted = (wsData) => {
      const exportData = wsData?.export;
      if (
        exportData &&
        (exportData.type === 'whatsapp_flow_responses' || exportData.flow_id) &&
        String(exportData.flow_id) === String(flow.meta_flow_id) &&
        (exportData.url || exportData.file_url)
      ) {
        const downloadUrl = exportData.url || exportData.file_url;
        const filename = `export-responses-${(flow.name || 'flow').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('target', '_blank');
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    };

    let intervalId = null;
    let subscribed = false;

    const trySubscribe = () => {
      // eslint-disable-next-line no-undef
      if (typeof socket !== 'undefined' && socket) {
        // eslint-disable-next-line no-undef
        socket.on('export_status_update', handleExportCompleted);
        subscribed = true;
        clearInterval(intervalId);
      }
    };

    trySubscribe();

    if (!subscribed) {
      let attempts = 0;
      intervalId = setInterval(() => {
        attempts += 1;
        trySubscribe();
        if (attempts >= 100) clearInterval(intervalId);
      }, 100);
    }

    return () => {
      clearInterval(intervalId);
      if (subscribed && typeof socket !== 'undefined' && socket) {
        // eslint-disable-next-line no-undef
        socket.off('export_status_update', handleExportCompleted);
      }
    };
  }, [flow.meta_flow_id, flow.name, t]);

  const uniqueResponseKeys = () => {
    const keys = new Set();
    responses.forEach((r) => {
      if (r.response_json && typeof r.response_json === "object") {
        Object.keys(r.response_json).forEach((k) => keys.add(k));
      }
    });
    return Array.from(keys);
  };

  const handleAddMapping = () => {
    if (!newFlowKey || !newCustomerField) {
      showtoast(t('validation.required_fields'), 'error');
      return;
    }
    setMappings([...mappings, { flowKey: newFlowKey, customerField: newCustomerField }]);
    setNewFlowKey("");
    setNewCustomerField("");
  };

  const handleRemoveMapping = (flowKeyToRemove) => {
    setMappings(mappings.filter(m => m.flowKey !== flowKeyToRemove));
  };

  const handleToggleSaveToCustomer = (val) => {
    setSaveToCustomerState(val);
  };

  const handleSaveMapping = async () => {
    setSaving(true);
    let fields_mapping = {};
    if (saveToCustomerState) {
      if (mappings.length === 0) {
        showtoast(t('validation.no_mappings'));
        setSaving(false);
        return;
      }
      mappings.forEach((m) => {
        fields_mapping[m.flowKey] = m.customerField;
      });
    }

    try {
      const response = await WhatsappFlowModelMC.put({
        id: flow.meta_flow_id,
        whatsapp_flow: {
          fields_mapping,
          default_header_text: defaultHeaderText,
          default_flow_cta: defaultFlowCta,
          default_body_text: defaultBodyText,
          default_footer_text: defaultFooterText
        }
      });
      showtoast(t('mapping_saved_success'));
      flow.fields_mapping = fields_mapping;
      flow.default_header_text = defaultHeaderText;
      flow.default_flow_cta = defaultFlowCta;
      flow.default_body_text = defaultBodyText;
      flow.default_footer_text = defaultFooterText;

      if (typeof onSaved === 'function') {
        const updatedFlow = response?.whatsapp_flow || response || flow;
        onSaved(updatedFlow);
      }

      closeModal();
    } catch (e) {
      console.error("Error saving mapping", e);
      showtoast(t('mapping_saved_error'));
    } finally {
      setSaving(false);
    }
  };

  const IGNORED_KEYS = useMemo(
    () =>
      new Set([
        "source",
        "flow_token",
        "flow_path",
        "navigate",
        "complete",
        "screen",
        "screen_id"
      ]),
    []
  );

  const flowKeys = useMemo(() => {
    const rawKeys = Array.from(new Set([...uniqueResponseKeys(), ...parsedFlowKeys]));
    return rawKeys.filter((key) => {
      if (!key) return false;
      return !IGNORED_KEYS.has(String(key).toLowerCase());
    });
  }, [responses, parsedFlowKeys, IGNORED_KEYS]);

  const responseKeys = useMemo(() => {
    const keys = new Set([
      ...parsedFlowKeys,
      ...uniqueResponseKeys()
    ]);
    return Array.from(keys).filter((key) => {
      if (!key) return false;
      return !IGNORED_KEYS.has(String(key).toLowerCase());
    });
  }, [responses, parsedFlowKeys, IGNORED_KEYS]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected + 1);
  };

  const handleDownloadCSV = async () => {
    try {
      const response = await WhatsappFlowModelMC.exportResponses(flow.meta_flow_id);
      const filename = `export-responses-${(flow.name || 'flow').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`;

      if (response && response.csv_data) {
        const blob = new Blob([response.csv_data], { type: 'text/csv;charset=utf-8;' });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else if (response && response.file_url) {
        const link = document.createElement('a');
        link.href = response.file_url;
        link.setAttribute('target', '_blank');
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      if (response?.message) {
        showtoast(response.message, 'success');
      }
    } catch (e) {
      console.error("Error initiating CSV download", e);
      showtoast(t('download_csv_error'), 'error');
    }
  };

  const availableFlowKeysOptions = flowKeys
    .filter(k => !mappings.some(m => m.flowKey === k))
    .map(k => ({ id: k, label: k }));

  const availableCustomerFieldsOptions = customerFieldsOptions.filter(
    option => option.id === 'customer_documents' || !mappings.some(m => m.customerField === option.id)
  );

  return {
    t,
    activeTab,
    setActiveTab,
    responses,
    loading,
    saving,
    saveToCustomerState,
    mappings,
    closeModal,
    handleAddMapping,
    handleRemoveMapping,
    handleToggleSaveToCustomer,
    handleSaveMapping,
    flowKeys,
    customerFieldsOptions,
    newFlowKey,
    setNewFlowKey,
    newCustomerField,
    setNewCustomerField,
    availableFlowKeysOptions,
    availableCustomerFieldsOptions,
    responseKeys,
    handleDownloadCSV,
    currentPage,
    totalPages,
    handlePageChange,
    dateRangeValue,
    handleDateRangeChange,
    defaultHeaderText,
    setDefaultHeaderText,
    defaultFlowCta,
    setDefaultFlowCta,
    defaultBodyText,
    setDefaultBodyText,
    defaultFooterText,
    setDefaultFooterText,
    methods
  };
};
